import React from "react"
import autobind from "class-autobind"
import strings from "../../localization/strings"
import { ELanguage, IQuestion, EQuestionAnswerType, EQuestionFrequency, ITriggerDuration } from "../../models"
import { Checkbox, Dialog, FlatButton, TextField, SelectField, MenuItem, Toggle, Table, TableHeader, TableHeaderColumn, TableBody, TableRow, TableRowColumn } from "material-ui"
import * as utils from "../../helpers/utils"
import _ from "lodash"
import moment from "moment"
import { EnumValues } from "enum-values"
import AddQuestionDialog from "./AddQuestionDialog"
import EditIcon from "material-ui/svg-icons/image/edit"
import { red500 } from "material-ui/styles/colors"
import QuestionPunchTriggerDuration from "./QuestionPunchTriggerDuration"

interface IProps {
    punchIn: boolean
    selectedLanguages: ELanguage[]
    newGroupId: number
    editQuestionGroupId: number
    questions: IQuestion[]
    handleAdd(questions: IQuestion[]): void
    handleUpdate(questions: IQuestion[]): void
    handleClose(): void
}
interface IState {
    name: string
    errName: string
    btn1Action: number
    createBtn1Exception: boolean
    btn2Action: number
    createBtn2Exception: boolean
    frequency: EQuestionFrequency
    child: boolean
    editQuestion: IQuestion
    questions: IQuestion[]
    errQuestion: boolean
    errPreventWarning: boolean
    triggerDuration: ITriggerDuration
}

export default class AddQuestionGroupDialog extends React.Component<IProps, IState> {
    private QuestionPunchTriggerDuration
    private defaultBtnAction = -3
    private defaultFrequency = EQuestionFrequency.OncePerDay
    private defaultChild = false
    constructor(props: IProps) {
        super(props)
        const editEngQuestionGroup = props.questions.find(i => i.GroupId === props.editQuestionGroupId && i.Language === ELanguage.English)
        this.state = {
            name: editEngQuestionGroup ? editEngQuestionGroup.Name : "",
            errName: "",
            btn1Action: editEngQuestionGroup ? editEngQuestionGroup.Btn1Action : this.defaultBtnAction,
            createBtn1Exception: editEngQuestionGroup ? editEngQuestionGroup.CreateBtn1Exception : false,
            btn2Action: editEngQuestionGroup ? editEngQuestionGroup.Btn2Action : this.defaultBtnAction,
            createBtn2Exception: editEngQuestionGroup ? editEngQuestionGroup.CreateBtn2Exception : false,
            frequency: editEngQuestionGroup ? editEngQuestionGroup.Frequency : this.defaultFrequency,
            child: editEngQuestionGroup ? editEngQuestionGroup.Child : this.defaultChild,
            editQuestion: null,
            questions: this.makeDefaultQuestions(),
            errQuestion: false,
            errPreventWarning: false,
            triggerDuration: editEngQuestionGroup ? editEngQuestionGroup.TriggerDuration : null,
        }
        autobind(this)
    }

    makeDefaultQuestions(): IQuestion[] {
        const questions = _.cloneDeep(this.props.questions.filter(i => i.GroupId === this.props.editQuestionGroupId)) || []
        const engQuestion = questions.find(i => i.Language === ELanguage.English)
        const groupId = this.props.editQuestionGroupId === null ? this.props.newGroupId : this.props.editQuestionGroupId
        return this.props.selectedLanguages.map(lang => {
            let question = questions.find(i => i.Language === lang && i.GroupId === groupId)
            if (!question) {
                const btn1Action = engQuestion ? engQuestion.Btn1Action : this.defaultBtnAction
                const btn2Action = engQuestion ? engQuestion.Btn2Action : this.defaultBtnAction
                question = {
                    GUId: utils.guid(),
                    GroupId: groupId,
                    Type: EQuestionAnswerType.Boolean,
                    Language: lang,
                    Body: null,
                    PunchIn: this.props.punchIn,
                    Title: null,
                    BtnLabel1: null,
                    BtnLabel2: null,
                    NotifyMsg: null,
                    Name: engQuestion ? engQuestion.Name : null,
                    Child: engQuestion ? engQuestion.Child : this.defaultChild,
                    Btn1Action: btn1Action,
                    CreateBtn1Exception: [-2, -1].includes(btn1Action),
                    Btn2Action: btn2Action,
                    CreateBtn2Exception: [-2, -1].includes(btn2Action),
                    Frequency: engQuestion ? engQuestion.Frequency : this.defaultFrequency,
                    TriggerDuration: null
                } as IQuestion
            }
            return question
        })
    }

    handleSave() {
        if (!this.checkValidate()) return
        this.props.editQuestionGroupId === null ?
            this.props.handleAdd(this.state.questions) :
            this.props.handleUpdate(this.state.questions)
    }

    checkValidate() {
        let isValid = true

        if (!this.state.name.trim()) {
            this.setState({ errName: strings.This_field_is_required })
            isValid = false
        }
        else {
            this.setState({ errName: "" })
        }

        const errQuestion = this.state.questions.find(i => !i.Body) ? true : false
        if (errQuestion) {
            isValid = false
        }
        this.setState({ errQuestion })

        if (this.state.btn1Action === -1 || this.state.btn2Action === -1) {
            const errPreventWarning = this.state.questions.find(i => !i.NotifyMsg) ? true : false
            if (errPreventWarning) {
                isValid = false
            }
            this.setState({ errPreventWarning })
        }

        if (!this.props.punchIn && !this.state.child && this.QuestionPunchTriggerDuration) {
            const errorTriggerDuration = this.QuestionPunchTriggerDuration.checkValudate()
            if (!errorTriggerDuration) {
                isValid = false
            }
        }
        return isValid
    }

    handleChangeGroupQuestions(name: string, value: any) {
        const questions = this.state.questions
        const modifiedDate = moment().utc().format()
        questions.forEach(i => {
            i.ModifiedDate = modifiedDate
            i[name] = value
            if (i.Btn1Action !== -1 && i.Btn2Action !== -1) {
                i.NotifyMsg = null
            }
            if ([-2, -1].includes(i.Btn1Action)) {
                i.CreateBtn1Exception = true
            }
            if ([-2, -1].includes(i.Btn2Action)) {
                i.CreateBtn2Exception = true
            }
        })

        if (name === "Child" && value === true) {
            questions.forEach(i => {
                i.TriggerDuration = null
            })
        }
        this.setState({ questions })
        this.checkValidate()
    }

    render() {
        return (
            <div>
                <Dialog
                    open
                    title={this.props.editQuestionGroupId !== null ?
                        this.props.punchIn ?
                            strings.Edit_Punch_IN_Question :
                            strings.Edit_Punch_OUT_Question :
                        this.props.punchIn ?
                            strings.New_Punch_IN_Question :
                            strings.New_Punch_OUT_Question
                    }
                    titleStyle={{ paddingLeft: 35, paddingBottom: 10 }}
                    actions={[
                        <FlatButton key="close" data-intercom-target={this.props.punchIn ? "PunchInQuestions-Btn-CloseAddDialog" : "PunchOutQuestions-Btn-CloseAddDialog"} label={strings.CANCEL} secondary onTouchTap={this.props.handleClose} />,
                        <FlatButton key="save" data-intercom-target={this.props.punchIn ? "PunchInQuestions-Btn-Save" : "PunchOutQuestions-Btn-Save"} label={strings.SAVE} primary onTouchTap={this.handleSave} />
                    ]}
                    contentStyle={{ width: "95%", maxWidth: "none" }}
                    autoScrollBodyContent
                >
                    <hr />
                    <div style={{ paddingLeft: 15, paddingRight: 15, paddingTop: 20 }}>
                        <div style={{ display: "flex" }}>
                            <div style={{ width: 260 }}>
                                <TextField
                                    fullWidth
                                    data-intercom-target={"PunchInQuestions-Entry-Name"}
                                    floatingLabelText={strings.Name}
                                    value={this.state.name}
                                    onChange={(e, value) => this.setState({ name: value }, () => this.handleChangeGroupQuestions("Name", value))}
                                    errorText={this.state.errName}
                                    errorStyle={{ position: "absolute", bottom: -10 }}
                                />
                                <div style={{ marginTop: 15 }}>
                                    <div style={{ fontSize: 12, color: "rgba(0,0,0,0.3)" }}>
                                        {strings.Workflow}
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <div style={{ width: 175, marginTop: -35 }}>{strings.Button1}</div>
                                        <div style={{ height: 90 }}>
                                            <SelectField
                                                autoWidth
                                                style={{ width: 175 }}
                                                value={this.state.btn1Action}
                                                onChange={(e, key, btn1Action) => this.setState({ btn1Action }, () => this.handleChangeGroupQuestions("Btn1Action", btn1Action))}
                                            >
                                                <MenuItem value={-3} primaryText={strings.None} />
                                                <MenuItem value={-2} primaryText={strings.Create_Exception} />
                                                <MenuItem value={-1} primaryText={strings.Exception_Prevent_Punch} />
                                                {this.props.questions
                                                    .filter(i => i.Language === ELanguage.English && i.Child && i.GroupId !== this.props.editQuestionGroupId)
                                                    .map((i, index) =>
                                                        <MenuItem value={i.GroupId} key={index} primaryText={i.Name} />
                                                    )
                                                }
                                            </SelectField>
                                            {this.state.btn1Action >= 0 &&
                                                <Checkbox
                                                    label={strings.Exception}
                                                    labelPosition="left"
                                                    checked={this.state.createBtn1Exception}
                                                    onCheck={(e, createBtn1Exception) => this.setState({ createBtn1Exception }, () => this.handleChangeGroupQuestions("CreateBtn1Exception", createBtn1Exception))}
                                                    style={{ marginTop: -15, width: 130, marginLeft: 40 }}
                                                />
                                            }
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <div style={{ width: 175, marginTop: -35 }}>{strings.Button2}</div>
                                        <div style={{ height: 90 }}>
                                            <SelectField
                                                autoWidth
                                                style={{ width: 175 }}
                                                value={this.state.btn2Action}
                                                onChange={(e, key, btn2Action) => this.setState({ btn2Action }, () => this.handleChangeGroupQuestions("Btn2Action", btn2Action))}
                                            >
                                                <MenuItem value={-3} primaryText={strings.None} />
                                                <MenuItem value={-2} primaryText={strings.Create_Exception} />
                                                <MenuItem value={-1} primaryText={strings.Exception_Prevent_Punch} />
                                                {this.props.questions
                                                    .filter(i => i.Language === ELanguage.English && i.Child && i.GroupId !== this.props.editQuestionGroupId)
                                                    .map((i, index) =>
                                                        <MenuItem value={i.GroupId} key={index} primaryText={i.Name} />
                                                    )
                                                }
                                            </SelectField>
                                            {this.state.btn2Action >= 0 &&
                                                <Checkbox
                                                    label={strings.Exception}
                                                    labelPosition="left"
                                                    checked={this.state.createBtn2Exception}
                                                    onCheck={(e, createBtn2Exception) => this.setState({ createBtn2Exception }, () => this.handleChangeGroupQuestions("CreateBtn2Exception", createBtn2Exception))}
                                                    style={{ marginTop: -15, width: 130, marginLeft: 40 }}
                                                />
                                            }
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <div style={{ width: 175, marginTop: -10 }}>{strings.Frequency}</div>
                                        <div>
                                            <SelectField
                                                autoWidth
                                                style={{ width: 175 }}
                                                value={this.state.frequency}
                                                onChange={(e, key, frequency) => this.setState({ frequency }, () => this.handleChangeGroupQuestions("Frequency", frequency))}
                                            >
                                                <MenuItem value={EQuestionFrequency.OncePerDay} primaryText={strings.Once_per_day} />
                                                <MenuItem value={EQuestionFrequency.OnEveryPunch} primaryText={strings.On_every_punch} />
                                            </SelectField>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div style={{ marginLeft: 50 }}>
                                <Table>
                                    <TableHeader
                                        displaySelectAll={false}
                                        adjustForCheckbox={false}
                                    >
                                        <TableRow >
                                            <TableHeaderColumn style={styles.langHeader}>{strings.Language}</TableHeaderColumn>
                                            <TableHeaderColumn style={styles.titleHeader}>{strings.Title}</TableHeaderColumn>
                                            <TableHeaderColumn style={styles.questionHeader}>{strings.Question}</TableHeaderColumn>
                                            <TableHeaderColumn style={styles.btnHeader}>{strings.Button1}</TableHeaderColumn>
                                            <TableHeaderColumn style={styles.btnHeader}>{strings.Button2}</TableHeaderColumn>
                                            {(this.state.btn1Action === -1 || this.state.btn2Action === -1) &&
                                                <TableHeaderColumn style={styles.preventHeader}>{strings.Prevent_Warning}</TableHeaderColumn>
                                            }
                                            <TableHeaderColumn style={styles.iconCol} />
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody displayRowCheckbox={false}>
                                        {this.props.selectedLanguages.map(lang => {
                                            const question = this.state.questions.find(i => i.Language === lang)
                                            const questionBody = question.Body && question.Body.replace(/(<([^>]+)>)/ig, '')
                                            const errQuestionBody = this.state.errQuestion && !questionBody ? true : false
                                            const errQuestionPreventWarning = !errQuestionBody && this.state.errPreventWarning && !question.NotifyMsg ? true : false
                                            return (
                                                <TableRow key={lang}>
                                                    <TableRowColumn style={styles.langCol}>{EnumValues.getNameFromValue(ELanguage, lang)}</TableRowColumn>
                                                    <TableRowColumn style={styles.titleCol}>{question.Title}</TableRowColumn>
                                                    <TableRowColumn style={{ ...styles.questionCol, color: errQuestionBody && red500, textAlign: errQuestionBody ? "center" : "left" }}>
                                                        {errQuestionBody ? strings.required : questionBody}
                                                    </TableRowColumn>
                                                    <TableRowColumn style={styles.btnCol}>{question.BtnLabel1}</TableRowColumn>
                                                    <TableRowColumn style={styles.btnCol}>{question.BtnLabel2}</TableRowColumn>
                                                    {(this.state.btn1Action === -1 || this.state.btn2Action === -1) &&
                                                        <TableRowColumn style={{ ...styles.preventCol, color: errQuestionPreventWarning && red500 }}>
                                                            <div dangerouslySetInnerHTML={{ __html: errQuestionPreventWarning ? strings.required : question.NotifyMsg }} />
                                                        </TableRowColumn>
                                                    }
                                                    <TableRowColumn style={styles.iconCol}>
                                                        <EditIcon onClick={() => this.setState({ editQuestion: question })} />
                                                    </TableRowColumn>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                        <div>
                            <Toggle
                                label={strings.Child_Question_Only}
                                labelStyle={{ fontSize: 16 }}
                                style={{ width: 200 }}
                                toggled={this.state.child}
                                onToggle={(e, child) => this.setState({ child }, () => this.handleChangeGroupQuestions("Child", child))}
                            />
                            {!this.props.punchIn && !this.state.child &&
                                <QuestionPunchTriggerDuration
                                    ref={item => this.QuestionPunchTriggerDuration = item}
                                    triggerDuration={this.state.triggerDuration}
                                    handleChange={(triggerDuration) => this.setState({ triggerDuration }, () => this.handleChangeGroupQuestions("TriggerDuration", triggerDuration))}
                                />
                            }
                        </div>
                    </div>
                </Dialog>
                {this.state.editQuestion &&
                    <AddQuestionDialog
                        question={this.state.editQuestion}
                        handleUpdate={question => {
                            const questions = this.state.questions
                            let indexOf = -1
                            questions.forEach((i, index) => {
                                if (i.GUId === question.GUId) {
                                    indexOf = index
                                }
                            })

                            if (indexOf > -1) {
                                questions[indexOf] = question
                            }

                            this.setState({ questions, editQuestion: null })
                        }}
                        handleClose={() => this.setState({ editQuestion: null })}
                    />
                }
            </div>
        )
    }
}

const styles = {
    langHeader: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 70,
        textAlign: "left"
    },
    langCol: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 70,
        textAlign: "left"
    },
    titleHeader: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 100
    },
    titleCol: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 100,
        textAlign: "left"
    },
    questionHeader: {
        paddingLeft: 15,
        paddingRight: 15,
        textAlign: "center"
    },
    questionCol: {
        paddingLeft: 15,
        paddingRight: 15
    },
    btnHeader: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 60
    },
    btnCol: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 60,
        textAlign: "left"
    },
    preventHeader: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 150
    },
    preventCol: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 150,
        textAlign: "left"
    },
    iconCol: {
        paddingLeft: 5,
        paddingRight: 5,
        width: 25
    }
}